// src/context/AuthContext.tsx
import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import type { Role } from "../types/roles";
import { jwtDecode } from "jwt-decode";
import { initAuthSilencioso, getAccessToken } from "../api/axios";

/* ─── tipos ─── */
export interface AuthUser {
  id: number;
  email: string;
  roles: Role[];
  // no exponer token aquí
  token?: string;
  mustChangePwd: boolean;
}

type DecodedJWT = {
  sub: number;
  email: string;
  rol?: Role | Role[]; // compat
  roles?: Role[]; // payload nuevo
  exp: number;
};

/* helpers */
function normalizeRoles(r: unknown): Role[] {
  if (Array.isArray(r)) return r as Role[];
  if (typeof r === "string" && r) return [r as Role];
  return [];
}

/* ─── contexto ─── */
const AuthContext = createContext<{
  user: AuthUser | null;
  setUser: (u: AuthUser | null) => void;
  bootstrapped: boolean;
}>({ user: null, setUser: () => {}, bootstrapped: false });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // 1) intenta renovar access token con la cookie httpOnly
        const token = await initAuthSilencioso(); // string | null
        // 2) si hay token en memoria, decodifica y rellena user
        if (token && getAccessToken()) {
          const { sub, email, rol, roles } = jwtDecode<DecodedJWT>(token);
          setUser({
            id: sub,
            email,
            roles: normalizeRoles(roles ?? rol),
            mustChangePwd: false, // claim no viene en JWT → false por defecto
          });
        } else {
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        setBootstrapped(true);
      }
    })();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, bootstrapped }}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export const useMustChangePwd = () => !!useAuth().user?.mustChangePwd;
